import { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FaSearch, FaMapMarkerAlt, FaUser, FaBuilding, FaCircle, FaExclamationTriangle, FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { getStatusPhaseColor } from '../../lib/workflowMapping'

// ── Skeleton loaders ────────────────────────────────────────────────────────
function SkeletonRow() {
    return (
        <div className="flex gap-3 items-center p-3 border-b border-gray-100 animate-pulse">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-1.5">
                <div className="h-3.5 bg-gray-200 rounded w-3/4" />
                <div className="w-1/2 h-3 bg-gray-100 rounded" />
            </div>
            <div className="w-16 h-5 bg-gray-200 rounded-full" />
        </div>
    )
}

function SkeletonCard() {
    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 animate-pulse">
            <div className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="w-9 h-9 bg-gray-200 rounded-full" />
                <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-gray-200 rounded w-2/3" />
                    <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                </div>
            </div>
            <div className="px-4 py-3 space-y-2">
                <div className="flex gap-2 items-center">
                    <div className="w-3 h-3 bg-gray-200 rounded-full shrink-0" />
                    <div className="flex-1 h-3 bg-gray-200 rounded" />
                    <div className="w-14 h-4 bg-gray-200 rounded-full shrink-0" />
                </div>
                <div className="flex gap-2 items-center pl-5">
                    <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                </div>
            </div>
        </div>
    )
}

// ── Project row ─────────────────────────────────────────────────────────────
function ProjectRow({ project, onSelect, t }) {
    const wfLabel = project.statusWfText?.startsWith('admin.workflow.') ? t(project.statusWfText) : (project.statusWfText || t('admin.workflow.no_status'))
    return (
        <button
            onClick={() => onSelect(project)}
            className="flex gap-3 items-center px-4 py-3 w-full text-left border-b border-gray-100 transition-colors hover:bg-gray-50 last:border-b-0 group"
        >
            <FaMapMarkerAlt className="text-gray-400 transition-colors shrink-0 group-hover:text-primary" size={14} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{project.name}</p>
                <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                    {project.clientName && (
                        <span className="flex gap-1 items-center">
                            <FaUser size={8} /> {project.clientName}
                        </span>
                    )}
                    {project.office && (
                        <span className="flex gap-1 items-center">
                            <FaBuilding size={8} /> {project.office}
                        </span>
                    )}
                </div>
            </div>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${getStatusPhaseColor(project)}`}>
                {wfLabel}
            </span>
        </button>
    )
}

// ── Client group (expandable — mobile) ──────────────────────────────────────
function ClientGroup({ displayName, nameVariants = [], projects, onSelect, t }) {
    const [expanded, setExpanded] = useState(projects.length <= 3)
    const hasMismatch = nameVariants.length > 1
    const tooltipText = hasMismatch
        ? `Nombres detectados: ${nameVariants.map(n => `'${n}'`).join(', ')}`
        : undefined

    return (
        <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
            <button
                onClick={() => setExpanded(e => !e)}
                className="flex gap-3 items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
            >
                <div className="flex justify-center items-center w-8 h-8 text-xs font-bold text-white bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full shrink-0">
                    {(displayName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{displayName || t('admin.workflow.no_name')}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-[11px] text-gray-500">{t('admin.workflow.project_count', { count: projects.length })}</p>
                        {hasMismatch && (
                            <span
                                title={tooltipText}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 cursor-help shrink-0"
                            >
                                {t('admin.workflow.name_mismatch')}
                            </span>
                        )}
                    </div>
                </div>
                {expanded ? <FaChevronDown size={10} className="text-gray-400" /> : <FaChevronRight size={10} className="text-gray-400" />}
            </button>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden border-t border-gray-100"
                    >
                        {projects.map(p => (
                            <ProjectRow key={p.id} project={p} onSelect={onSelect} t={t} />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// ── Card project row (shared by ClientCard) ────────────────────────────────
function CardProjectRow({ project, onSelect, t }) {
    const wfLabel = project.statusWfText?.startsWith('admin.workflow.') ? t(project.statusWfText) : (project.statusWfText || t('admin.workflow.no_status'))
    return (
        <button
            onClick={() => onSelect(project)}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors group"
        >
            <FaMapMarkerAlt className="text-gray-400 transition-colors shrink-0 group-hover:text-primary" size={12} />
            <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-xs font-semibold leading-snug text-gray-800 truncate">{project.name}</p>
                <p className="text-[10px] text-gray-500 truncate mt-0.5">
                    {project.clientName && <><FaUser size={7} className="inline mr-0.5 -mt-px" />{project.clientName}</>}
                    {project.clientName && project.office && <span className="mx-1">·</span>}
                    {project.office && <><FaBuilding size={7} className="inline mr-0.5 -mt-px" />{project.office}</>}
                </p>
            </div>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${getStatusPhaseColor(project)}`}>
                {wfLabel}
            </span>
        </button>
    )
}

// ── Client card (desktop grid) ──────────────────────────────────────────────
function ClientCard({ displayName, nameVariants = [], projects, onSelect, t }) {
    const [expanded, setExpanded] = useState(false)
    const first = projects[0]
    const rest = projects.slice(1)
    const hasMore = rest.length > 0
    const hasMismatch = nameVariants.length > 1
    const tooltipText = hasMismatch
        ? `Nombres detectados: ${nameVariants.map(n => `'${n}'`).join(', ')}`
        : undefined

    return (
        <div className="flex overflow-hidden flex-col bg-white rounded-2xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
            {/* Header */}
            <div className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex justify-center items-center w-9 h-9 text-sm font-bold text-white bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full shrink-0">
                    {(displayName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{displayName || t('admin.workflow.no_name')}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-[11px] text-gray-500">{t('admin.workflow.project_count', { count: projects.length })}</p>
                        {hasMismatch && (
                            <span
                                title={tooltipText}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 cursor-help shrink-0"
                            >
                                {t('admin.workflow.name_mismatch')}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {/* First project always visible */}
            <div className="divide-y divide-gray-100">
                <CardProjectRow project={first} onSelect={onSelect} t={t} />
            </div>
            {/* Expandable rest */}
            {hasMore && (
                <>
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-gray-100 divide-y divide-gray-100"
                            >
                                {rest.map(p => (
                                    <CardProjectRow key={p.id} project={p} onSelect={onSelect} t={t} />
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <button
                        onClick={() => setExpanded(e => !e)}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                    >
                        {expanded ? (
                            <><FaChevronDown size={8} className="transition-transform rotate-180" /> {t('admin.workflow.hide')}</>
                        ) : (
                            <><FaChevronDown size={8} /> {t('admin.workflow.show_more', { count: rest.length })}</>
                        )}
                    </button>
                </>
            )}
        </div>
    )
}

// ── Board filter chip definitions ──────────────────────────────────────────
const BOARD_FILTERS = [
    { id: '18396594554', key: 'office_miami',  active: 'bg-blue-600 text-white border-blue-600 shadow-sm',  inactive: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' },
    { id: '18396592406', key: 'office_dallas', active: 'bg-amber-500 text-white border-amber-500 shadow-sm', inactive: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100' },
]

// ── Status filter chip definitions (4 filters, always colored) ─────────────
const STATUS_FILTER_TABS = [
    { id: 5,  labelKey: 'filter_pm_review',  inactive: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100',   active: 'bg-amber-400 text-white border-amber-400 shadow-md' },
    { id: 19, labelKey: 'filter_2nd_pay',    inactive: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100',   active: 'bg-amber-400 text-white border-amber-400 shadow-md' },
    { id: 33, labelKey: 'filter_3rd_pay',    inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-500 text-white border-emerald-500 shadow-md' },
    { id: 39, labelKey: 'filter_delivered',  inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-500 text-white border-emerald-500 shadow-md' },
    { id: 40, labelKey: 'filter_suspended',  inactive: 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100',     active: 'bg-rose-500 text-white border-rose-500 shadow-md' },
]

// ── Main component ──────────────────────────────────────────────────────────
export default function ProjectPicker({ projects, loading, error, onSelect, isPm, refreshing = false }) {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')
    const [boardFilter, setBoardFilter] = useState(null)
    const [statusFilter, setStatusFilter] = useState(null)
    const searchRef = useRef(null)

    useEffect(() => {
        searchRef.current?.focus()
    }, [])

    // Reset status filter when board changes to avoid stale 0-result state
    const handleBoardFilter = (id) => {
        setBoardFilter(id)
        setStatusFilter(null)
    }

    const filtered = useMemo(() => {
        let result = projects
        if (boardFilter) {
            result = result.filter(p => p.boardId === boardFilter)
        }
        if (search.trim()) {
            const q = search.toLowerCase()
            result = result.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.clientName || '').toLowerCase().includes(q) ||
                (p.office || '').toLowerCase().includes(q)
            )
        }
        if (statusFilter !== null) {
            result = result.filter(p => p.statusWfId === statusFilter)
        }
        return result
    }, [projects, boardFilter, search, statusFilter])

    // Projects scoped to the active board filter — used for status chip counts
    const boardFiltered = useMemo(() =>
        boardFilter ? projects.filter(p => p.boardId === boardFilter) : projects
    , [projects, boardFilter])

    // Group by email (smart dedup) — falls back to clientName when no email
    const grouped = useMemo(() => {
        if (!isPm) return null
        const map = new Map()
        filtered.forEach(p => {
            const email = p.clientEmail?.trim().toLowerCase()
            const key = email || p.clientName || '__none__'
            if (!map.has(key)) map.set(key, { projects: [], names: new Set() })
            map.get(key).projects.push(p)
            if (p.clientName?.trim()) map.get(key).names.add(p.clientName.trim())
        })
        return Array.from(map.values())
            .map(({ projects, names }) => {
                const nameVariants = Array.from(names)
                const displayName = nameVariants.length > 0
                    ? nameVariants.reduce((a, b) => a.length >= b.length ? a : b)
                    : t('admin.workflow.no_name')
                return { displayName, nameVariants, projects }
            })
            .filter(g => g.projects.length > 0)
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
    }, [filtered, isPm, t])

    return (
        <div className="mx-auto max-w-2xl lg:max-w-5xl">
            {/* Search bar */}
            <div className="relative mb-4">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('admin.workflow.search_projects')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                />
                {search && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                        {filtered.length} de {boardFiltered.length}
                    </span>
                )}
            </div>

            {/* Board (office) filter chips — PM only */}
            {isPm && projects.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-2 scrollbar-none">
                    <button
                        onClick={() => handleBoardFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            boardFilter === null
                                ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{projects.length}</span>
                    </button>
                    {BOARD_FILTERS.map(bf => {
                        const count = projects.filter(p => p.boardId === bf.id).length
                        const isActive = boardFilter === bf.id
                        return (
                            <button
                                key={bf.id}
                                onClick={() => handleBoardFilter(isActive ? null : bf.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive ? bf.active : bf.inactive
                                }`}
                            >
                                {t(`admin.workflow.${bf.key}`)}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Status filter chips — PM only, always colored; shown as soon as we have data */}
            {isPm && projects.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 scrollbar-none">
                    {/* All chip */}
                    <button
                        onClick={() => setStatusFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            statusFilter === null
                                ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{boardFiltered.length}</span>
                    </button>
                    {STATUS_FILTER_TABS.map(tab => {
                        const count    = boardFiltered.filter(p => p.statusWfId === tab.id).length
                        const isActive = statusFilter === tab.id
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setStatusFilter(isActive ? null : tab.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive ? tab.active : tab.inactive
                                }`}
                            >
                                {t(`admin.workflow.${tab.labelKey}`)}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Loading — skeleton only on cold load (no data yet) */}
            {loading && projects.length === 0 && (
                <>
                    {/* Desktop: card skeleton grid */}
                    <div className="hidden grid-cols-2 gap-3 lg:grid xl:grid-cols-3">
                        {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                    {/* Mobile: list skeleton */}
                    <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm lg:hidden">
                        {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
                    </div>
                </>
            )}

            {/* Error */}
            {error && !loading && (
                <div className="flex flex-col gap-3 items-center py-12 text-center">
                    <FaExclamationTriangle className="text-3xl text-amber-500" />
                    <p className="text-sm font-medium text-gray-600">{t('admin.workflow.error_load')}</p>
                    <p className="max-w-sm text-xs text-gray-400">{error}</p>
                </div>
            )}

            {/* Empty */}
            {!loading && !error && projects.length === 0 && (
                <div className="flex flex-col gap-3 items-center py-12 text-center">
                    <FaBuilding className="text-3xl text-gray-300" />
                    <p className="text-sm font-medium text-gray-500">{t('admin.workflow.no_projects')}</p>
                </div>
            )}

            {/* No search results */}
            {!loading && !error && projects.length > 0 && filtered.length === 0 && (
                <div className="flex flex-col gap-2 items-center py-8 text-center">
                    <FaSearch className="text-2xl text-gray-300" />
                    <p className="text-sm text-gray-500">{t('admin.workflow.no_search_results', { search })}</p>
                </div>
            )}

            {/* Project list */}
            {!loading && !error && filtered.length > 0 && (
                <>
                    {isPm && grouped ? (
                        <>
                            {/* Desktop: card grid */}
                            <div className="hidden grid-cols-2 gap-3 lg:grid xl:grid-cols-3">
                                {grouped.map((g) => (
                                    <ClientCard
                                        key={g.displayName}
                                        displayName={g.displayName}
                                        nameVariants={g.nameVariants}
                                        projects={g.projects}
                                        onSelect={onSelect}
                                        t={t}
                                    />
                                ))}
                            </div>
                            {/* Mobile: stacked accordion */}
                            <div className="flex flex-col gap-2 lg:hidden">
                                {grouped.map((g) => (
                                    <ClientGroup
                                        key={g.displayName}
                                        displayName={g.displayName}
                                        nameVariants={g.nameVariants}
                                        projects={g.projects}
                                        onSelect={onSelect}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="overflow-hidden mx-auto max-w-2xl bg-white rounded-xl border border-gray-200 shadow-sm">
                            {filtered.map(p => (
                                <ProjectRow key={p.id} project={p} onSelect={onSelect} t={t} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

// ── Quick-switch search (for WorkflowMap header) ────────────────────────────
export function QuickProjectSearch({ projects, currentProject, onSwitch }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const wrapperRef = useRef(null)
    const inputRef = useRef(null)

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (open) inputRef.current?.focus()
    }, [open])

    const filtered = useMemo(() => {
        if (!search.trim()) return projects
        const q = search.toLowerCase()
        return projects.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.clientName || '').toLowerCase().includes(q) ||
            (p.office || '').toLowerCase().includes(q)
        )
    }, [projects, search])

    return (
        <div ref={wrapperRef} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors max-w-[240px]"
            >
                <FaSearch size={10} className="text-gray-400 shrink-0" />
                <span className="text-xs font-medium truncate">{currentProject?.name || t('admin.workflow.select_project')}</span>
                <FaChevronDown size={8} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden absolute left-0 top-full z-50 mt-1 w-72 bg-white rounded-xl border border-gray-200 shadow-xl"
                    >
                        <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={10} />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder={t('admin.workflow.search_project_short')}
                                    className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/50"
                                />
                            </div>
                        </div>
                        <div className="overflow-y-auto max-h-60">
                            {filtered.length === 0 ? (
                                <p className="py-4 text-xs text-center text-gray-400">{t('admin.workflow.no_results_short')}</p>
                            ) : (
                                filtered.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => { onSwitch(p); setOpen(false); setSearch('') }}
                                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-gray-50 last:border-0 ${p.id === currentProject?.id ? 'bg-primary/5' : ''}`}
                                    >
                                        <FaCircle size={6} className={p.id === currentProject?.id ? 'text-primary' : 'text-gray-300'} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-800 truncate">{p.name}</p>
                                            <p className="text-[10px] text-gray-400 truncate">{p.clientName || '—'} · {p.office || 'N/A'}</p>
                                        </div>
                                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${getStatusPhaseColor(p)}`}>
                                            {p.statusWfText?.startsWith('admin.workflow.') ? t(p.statusWfText) : (p.statusWfText || '—')}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
